import {
  DEFAULT_PAYMENT_LINK_ADJUSTABLE_MAXIMUM,
  PAYMENT_LINK_ADJUSTABLE_MAXIMUM_LIMIT,
  ListActivePaymentLinksApiOptions,
  ListPaymentLinkLineItemsResponse,
  ListPaymentLinksResponse,
  PaymentLinkCustomField,
  PaymentLinkDesiredItem,
  PaymentLinkEditPayload,
  PaymentLinkLineItem,
  PaymentLinkLineItemPayload,
  PaymentLinkPayload,
  PaymentLinks,
  PaymentLinksStore,
  SendPaymentLinkPayload,
} from "@/payment-links/types/paymentLinks";
import { defineStore } from "pinia";
import { useSnackBarStore } from "@/shared/stores/snackbar";
import axios from "@/app/plugins/axios";
import i18n from "@/app/plugins/i18n";
import { getErrorMessage } from "@/shared/utils/errorMessage";
import type { AxiosRequestConfig } from "axios";

export const usePaymentLinksStore = defineStore("paymentLinks", {
  state: (): PaymentLinksStore => ({
    loading: false,
    selectedPaymentLink: null,
    paymentLinks: [],
    allPaymentLinks: [],
    isActive: null,
    loadingListAllPaymentLinks: false,
    paymentLinkPayload: {
      metadata: {
        // current date in seconds
        created: Math.floor(Date.now() / 1000),
      },
      line_items: [],
    },
    creatingPaymentLinkLoading: false,
    creationKey: null,
    selectedLineItems: [],
    editPaymentLinkPayload: {},
    hasMore: false,
    startingAfter: "",
    endingBefore: "",
    metadataName: "",
    paymentLinkValidation: {
      customFields: true,
      confirmationPage: true,
      paymentLimit: true,
    },
  }),

  getters: {
    isLoading: (state) => state.loading,
    paymentLinkValid: (state) => {
      const payload = state.selectedPaymentLink
        ? state.editPaymentLinkPayload
        : state.paymentLinkPayload;
      const required = payload.line_items || [];
      const optional = payload.optional_items || [];
      const validQuantity = (item: PaymentLinkLineItemPayload) => {
        const quantity = Number(item.quantity);
        if (!Number.isInteger(quantity) || quantity < 1) return false;
        if (!item.adjustable_quantity?.enabled) return true;
        const minimum = Number(item.adjustable_quantity.minimum ?? 1);
        const maximum = Number(
          item.adjustable_quantity.maximum ?? DEFAULT_PAYMENT_LINK_ADJUSTABLE_MAXIMUM,
        );
        return (
          Number.isInteger(minimum) &&
          Number.isInteger(maximum) &&
          minimum >= 1 &&
          minimum <= quantity &&
          quantity <= maximum &&
          maximum <= PAYMENT_LINK_ADJUSTABLE_MAXIMUM_LIMIT
        );
      };
      return (
        (state.selectedPaymentLink ? true : required.length > 0) &&
        optional.length <= 10 &&
        required.length + optional.length <= 20 &&
        [...required, ...optional].every(validQuantity) &&
        state.paymentLinkValidation.customFields &&
        state.paymentLinkValidation.confirmationPage &&
        state.paymentLinkValidation.paymentLimit
      );
    },
  },

  actions: {
    addPaymentLinkLineItems(newLineItems: PaymentLinkLineItemPayload[]) {
      newLineItems.forEach((newItem) => {
        const existingItemIndex = this.paymentLinkPayload.line_items.findIndex(
          (item) => item.price === newItem.price,
        );

        if (existingItemIndex !== -1) {
          // Update existing line item's quantity and adjustable_quantity
          const existingItem = this.paymentLinkPayload.line_items[existingItemIndex];
          existingItem.quantity = newItem.quantity;
          existingItem.adjustable_quantity = newItem.adjustable_quantity; // Always update, even if it's undefined
        } else {
          // Add new line item if not already existing
          this.paymentLinkPayload.line_items.push(newItem);
        }
      });
    },
    removePaymentLinkLineItem(priceId: string) {
      const index = this.paymentLinkPayload.line_items.findIndex((item) => item.price === priceId);
      if (index !== -1) {
        this.paymentLinkPayload.line_items.splice(index, 1);
      }
    },

    addPaymentLinkCustomFields(fields: PaymentLinkCustomField[]) {
      this.paymentLinkPayload.custom_fields = [];
      fields.forEach((field) => {
        this.paymentLinkPayload.custom_fields?.push(field);
      });
    },
    setPaymentOptions(options: Partial<PaymentLinkPayload>) {
      this.paymentLinkPayload = {
        ...this.paymentLinkPayload,
        ...options,
      };
    },

    setUpdatedPayload(options: PaymentLinkEditPayload) {
      this.editPaymentLinkPayload = {
        ...this.editPaymentLinkPayload,
        ...options,
      };
    },

    async listPaymentLinks(apiOptions?: AxiosRequestConfig): Promise<boolean> {
      try {
        this.loading = true;
        const response = await axios.get<ListPaymentLinksResponse>(
          this.isActive === null ? "/paymentLink" : `/paymentLink?active=${this.isActive}`,
          apiOptions,
        );

        this.hasMore = response.data.has_more;
        this.startingAfter = response.data.data[response.data.data.length - 1]?.id;
        this.endingBefore = response.data.data[0]?.id;
        this.paymentLinks = response.data.data;
        return true;
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
        return false;
      } finally {
        this.loading = false;
      }
    },

    async listActivePaymentLinks(
      apiOptions?: ListActivePaymentLinksApiOptions,
    ): Promise<ListPaymentLinksResponse | undefined> {
      try {
        this.loading = true;

        const response = await axios.get<ListPaymentLinksResponse>(
          `/paymentLink?active=true&limit=${
            apiOptions?.limit || 10
          }&starting_after=${apiOptions?.starting_after || ""}`,
        );
        return response.data;
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
      } finally {
        this.loading = false;
      }
    },

    setIsActive(value: boolean | null): void {
      this.isActive = value;
    },

    async getAllPaymentLinks(): Promise<void> {
      try {
        this.loadingListAllPaymentLinks = true;
        const activeLinks = await axios.get<ListPaymentLinksResponse>("/paymentLink?active=true");
        const inactiveLinks = await axios.get<ListPaymentLinksResponse>(
          "/paymentLink?active=false",
        );
        this.allPaymentLinks = [...activeLinks.data.data, ...inactiveLinks.data.data];
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
      } finally {
        this.loadingListAllPaymentLinks = false;
      }
    },

    async retrievePaymentLink(id: string, background = false): Promise<PaymentLinks | undefined> {
      try {
        if (!background) this.loading = true;
        const response = await axios.get<PaymentLinks>(`/paymentLink/${id}`);
        this.selectedPaymentLink = response.data;
        const metadata = response.data.metadata || {};
        const stripeItems = response.data.line_items?.data || [];
        const stripeItemsByPriceId = new Map<string, PaymentLinkLineItem>(
          stripeItems.map((item) => [item.price.id, item]),
        );
        const stripePriceIds = new Set([
          ...stripeItemsByPriceId.keys(),
          ...(response.data.optional_items || [])
            .map((item) => item.price)
            .filter((priceId): priceId is string => typeof priceId === "string"),
        ]);
        const hasManagedDesiredItems = Array.isArray(response.data.nectarpay?.desiredItems);
        const desiredItems =
          response.data.nectarpay?.desiredItems ||
          stripeItems.map(
            (item): PaymentLinkDesiredItem => ({
              priceId: item.price.id,
              productId:
                typeof item.price.product === "string"
                  ? item.price.product
                  : item.price.product?.id || "",
              productName:
                typeof item.price.product === "object" ? item.price.product?.name : undefined,
              priceNickname: item.price.nickname,
              unitAmount: item.price.unit_amount,
              currency: item.price.currency,
              quantity: item.quantity,
              kind: "required",
              stripeLineItemId: item.id,
              adjustableQuantity: item.adjustable_quantity?.enabled
                ? {
                    enabled: true,
                    minimum: item.adjustable_quantity.minimum ?? 1,
                    maximum:
                      item.adjustable_quantity.maximum ?? DEFAULT_PAYMENT_LINK_ADJUSTABLE_MAXIMUM,
                  }
                : undefined,
            }),
          );
        const effectiveItems = response.data.nectarpay?.effectiveItems || [];
        const effectiveByPriceId = new Map<string, PaymentLinkDesiredItem>(
          effectiveItems.map((item) => [item.priceId, item]),
        );
        this.selectedLineItems = desiredItems.map((desired) => {
          const item = stripeItemsByPriceId.get(desired.priceId);
          const effective = effectiveByPriceId.get(desired.priceId);
          const isAdjustable = desired.adjustableQuantity
            ? true
            : !hasManagedDesiredItems && metadata[desired.priceId] !== "false";
          const adjustable = desired.adjustableQuantity
            ? `0|${desired.adjustableQuantity.minimum}|${desired.adjustableQuantity.maximum}`
            : hasManagedDesiredItems
              ? undefined
              : metadata[desired.priceId];
          return {
            ...item,
            id: item?.id || desired.stripeLineItemId || desired.priceId,
            description: item?.description || desired.productName || "-",
            price: item?.price || {
              id: desired.priceId,
              product: { id: desired.productId, name: desired.productName },
              unit_amount: desired.unitAmount,
              currency: desired.currency,
              nickname: desired.priceNickname,
            },
            quantity: desired.quantity,
            productId: desired.productId,
            priceId: desired.priceId,
            optional: desired.kind === "optional",
            isAvailableInStripe: stripePriceIds.has(desired.priceId),
            isAdjustable,
            adjustable,
            configuredQuantity: desired.quantity,
            configuredAdjustableQuantity: desired.adjustableQuantity,
            available: effective?.available,
            effectiveQuantity: effective?.effectiveQuantity,
            effectiveMaximum: effective?.effectiveMaximum,
            availabilityReason: effective?.availabilityReason,
          };
        });
        return response.data;
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
      } finally {
        if (!background) this.loading = false;
      }
    },

    async retrievePaymentLinkLineItems(
      id: string,
    ): Promise<ListPaymentLinkLineItemsResponse | undefined> {
      try {
        this.loading = true;
        const response = await axios.get<ListPaymentLinkLineItemsResponse>(
          `/paymentLink/${id}/lineItems`,
        );
        return response.data;
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
      } finally {
        this.loading = false;
      }
    },

    async generatePaymentLink(): Promise<PaymentLinks | undefined> {
      if (this.creatingPaymentLinkLoading) return;
      this.creationKey ||= crypto.randomUUID();
      try {
        this.creatingPaymentLinkLoading = true;
        const response = await axios.post<PaymentLinks>("/paymentLink", this.paymentLinkPayload, {
          headers: { "Idempotency-Key": this.creationKey },
        });
        useSnackBarStore().toastSuccess({
          message: i18n.global.t("page.paymentLinks.message.created"),
        });
        this.resetPaymentLink();
        return response.data;
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
      } finally {
        this.creatingPaymentLinkLoading = false;
      }
    },

    async updatePaymentLink(id: string): Promise<PaymentLinks | undefined> {
      try {
        this.creatingPaymentLinkLoading = true;
        const response = await axios.put<PaymentLinks>(
          `/paymentLink/${id}`,
          this.editPaymentLinkPayload,
        );
        useSnackBarStore().toastSuccess({
          message: i18n.global.t("page.paymentLinks.message.updated"),
        });
        return response.data;
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
      } finally {
        this.resetPaymentLink();
        this.creatingPaymentLinkLoading = false;
      }
    },

    async activatePaymentLink(
      id: string,
      active: boolean,
      all?: boolean,
    ): Promise<PaymentLinks | undefined> {
      try {
        this.loading = true;
        const response = await axios.put<PaymentLinks>(`/paymentLink/${id}`, {
          active,
        });
        const message = active
          ? i18n.global.t("page.paymentLinks.message.activated")
          : i18n.global.t("page.paymentLinks.message.deactivated");

        useSnackBarStore().toastSuccess({
          message,
        });

        if (all) {
          await this.listPaymentLinks();
        } else {
          await this.retrievePaymentLink(id);
        }
        return response.data;
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
      } finally {
        this.loading = false;
      }
    },

    resetPaymentLink(): void {
      this.creationKey = null;
      this.selectedPaymentLink = null;
      this.selectedLineItems = [];
      this.paymentLinkPayload = {
        metadata: {
          name: "",
          created: Math.floor(Date.now() / 1000),
        },
        line_items: [],
      };
      this.editPaymentLinkPayload = {};
    },

    async sendPaymentLink(payload: SendPaymentLinkPayload): Promise<void> {
      try {
        this.loading = true;
        await axios.post("/paymentLink/send", payload);

        useSnackBarStore().toastSuccess({
          message: i18n.global.t("page.paymentLinks.message.send"),
        });
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
      } finally {
        this.loading = false;
      }
    },
  },
});
